/**
 * 网页版的学习模块登记表（对应 iOS ActivityCatalog + 各模块 ActivityModule 的可选方法，docs/插件开发.md）：
 * 名称、配色、payload Schema 来自注册表（activities/registry.ts），这里只放网页版自己的界面和少量逻辑。
 * 新增模块：注册表里登记后，网页版就能显示名称、色块和按 Schema 生成的布置设置；要在网页上新建或预览，再在这里加一行。
 */
import type { ReactNode } from "react";
import type { Content, JSONObject } from "../../core";
import { charCount, isObject, payloadOf, str } from "./activity";
import { type EditorProps, HandwritingEditor, PdfEditor, VideoEditor } from "./editors";
import { GameEditor } from "./GameEditor";
import { pdfPageCount } from "./pdf";
import { HandwritingPreview, PdfPreview, VideoPreview, WebActivityInfo } from "./previews";

export interface WebModule {
  /** 新建内容的编辑器（没有就不能在网页上新建）；互动游戏是"游戏库 → 设置" */
  Editor?: (p: EditorProps) => ReactNode;
  /** 家长预览 */
  Preview?: (p: { content: Content }) => ReactNode;
  /** 内容列表、布置页副标题里的一句话（"6 个字"） */
  summary?: (c: Pick<Content, "activityType" | "payload">) => string | null;
  /** 新建时的默认标题（fileName 是最近上传的文件名） */
  defaultTitle?: (payload: JSONObject, fileName: string | null) => string;
  /** 布置页设置表单的提示值（PDF 的 page_count） */
  assignHints?: (c: Content) => Promise<Record<string, number>>;
}

const fromFileName = (_: JSONObject, fileName: string | null) => (fileName ? fileName.replace(/\.[^.]+$/, "") : "");

export const WEB_MODULES: Record<string, WebModule> = {
  pdf_reader: {
    Editor: PdfEditor,
    Preview: PdfPreview,
    defaultTitle: fromFileName,
    assignHints: async (c): Promise<Record<string, number>> => {
      const id = str(payloadOf(c).pdf_asset_id);
      return id ? { page_count: await pdfPageCount(id) } : {};
    },
  },
  video_player: { Editor: VideoEditor, Preview: VideoPreview, defaultTitle: fromFileName },
  handwriting: {
    Editor: HandwritingEditor,
    Preview: HandwritingPreview,
    summary: (c) => {
      const n = charCount(c);
      return n != null ? `${n} 个字` : null;
    },
    defaultTitle: (p) => (Array.isArray(p.characters) ? `写字练习 · ${p.characters.map((c) => (isObject(c) ? (str(c.char) ?? "") : "")).join("")}` : ""),
  },
  web_activity: { Editor: GameEditor, Preview: WebActivityInfo },
};

export function webModule(type: string): WebModule {
  return WEB_MODULES[type] ?? {};
}
